import React, { useState } from "react";
import { Table, Title, Field, Control, Button } from "rbx";
import PropTypes from "prop-types";

import { useMutation } from "react-apollo";
import HorizontalRule from "../../../../../styles/HorizontalRule";
import { ReactTable } from "../../../../../components/DataTable/DataTable";
import { getCustomerValue } from "../../../../../utils";

import { BILLED_ROUTE_ORDER_MUTATION } from "../../../../../graphql/Orders/Orders.graphql";
import {
  PRODUCT_DETAIL_BILLING_COLUMNS,
  ORDER_DETAIL_BILLING_COLUMNS
} from "./columns";

import "./BilledRouteRefreshModal.scss";

const BillingRouteCompareModal = ({
  order,
  route,
  orderDetails,
  routeProducts,
  onComplete,
  onCompleteWithoutSync,
  onCancel
}) => {
  const [saving, setSaving] = useState(false);

  const [updateOrder, { loading: loadingUpdate }] = useMutation(
    BILLED_ROUTE_ORDER_MUTATION,
    {
      refetchQueries: ["SINGLE_ORDER_QUERY", "ALL_ORDERS_QUERY"]
    }
  );

  const handleComplete = async () => {
    setSaving(true);
    await updateOrder({
      variables: {
        where: { RecId: order.RecId }
      }
    });
    await onComplete();
    setSaving(false);
  };

  const handleCompleteWithoutSync = async () => {
    setSaving(true);
    await onCompleteWithoutSync();
    setSaving(false);
  };

  const fieldsToCompare = ["Bill To", "Load Type", "Payment Term", "Tariff"];

  const getRowData = (field, type) =>
    ({
      "Bill To": getCustomerValue(
        type === "order" ? order.BillTo : route.BillTo
      ),
      Contract: type === "order" ? order.Route.Contract : route.Contract,
      "Load Type":
        type === "order"
          ? order.LoadType?.CodeDescription
          : route.LoadType?.CodeDescription,
      "Payment Term":
        type === "order"
          ? order.PaymentTerm?.TermDescription
          : route?.PaymentTerm?.TermDescription,
      Tariff:
        type === "order"
          ? [order.Tariff?.TariffCode, order.Tariff?.Description]
              .filter(Boolean)
              .join(" ")
          : [route.Tariff?.TariffCode, route.Tariff?.Description]
              .filter(Boolean)
              .join(" ")
    }[field]);

  return (
    <div className="billing-route-compare-container">
      <Title color="primary" size={3}>
        Billed Route Refresh
      </Title>
      <Table bordered fullwidth hoverable narrow>
        <Table.Head>
          <Table.Row>
            <Table.Heading className="header"></Table.Heading>
            <Table.Heading className="header">
              Order #{order.OrderNumber}
            </Table.Heading>
            <Table.Heading className="header">
              Route {route.RouteDescription}
            </Table.Heading>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {fieldsToCompare.map(field => {
            const orderData = getRowData(field, "order");
            const routeData = getRowData(field, "route");
            return (
              <Table.Row key={field}>
                <Table.Cell className="field-name">{field}</Table.Cell>
                <Table.Cell
                  className={orderData !== routeData ? "different" : "equal"}
                >
                  {orderData || "-"}
                </Table.Cell>
                <Table.Cell
                  className={orderData !== routeData ? "different" : "equal"}
                >
                  {routeData || "-"}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <div className="products-container">
        <div>
          <Title color="primary" size={6}>
            Order Details
          </Title>
          <ReactTable
            columns={ORDER_DETAIL_BILLING_COLUMNS}
            data={orderDetails}
            initialSortBy={[
              { id: "ProductCategoryDescription", desc: true },
              { id: "ProductProductCode", desc: true }
            ]}
          />
        </div>
        <div>
          <Title color="primary" size={6}>
            Route Products
          </Title>
          <ReactTable
            columns={PRODUCT_DETAIL_BILLING_COLUMNS(orderDetails)}
            data={routeProducts}
            initialSortBy={[
              { id: "ProductCategoryDescription", desc: true },
              { id: "ProductProductCode", desc: true }
            ]}
          />
        </div>
      </div>
      <HorizontalRule />
      <Title color="primary" size={6}>
        Would you like to refresh order information from route before
        calculating?
      </Title>
      <Field kind="group">
        <Control>
          <Button
            color="success"
            state={loadingUpdate || saving ? "loading" : ""}
            onClick={handleComplete}
          >
            Yes
          </Button>
        </Control>
        <Control>
          <Button
            color="primary"
            state={loadingUpdate || saving ? "loading" : ""}
            onClick={handleCompleteWithoutSync}
          >
            No
          </Button>
        </Control>
        <Control>
          <Button
            disabled={loadingUpdate || saving}
            type="danger"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Control>
      </Field>
    </div>
  );
};

BillingRouteCompareModal.propTypes = {
  order: PropTypes.object,
  route: PropTypes.object,
  orderDetails: PropTypes.array,
  routeProducts: PropTypes.array,
  onComplete: PropTypes.func,
  onCompleteWithoutSync: PropTypes.func,
  onCancel: PropTypes.func
};

export default BillingRouteCompareModal;
